import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, renderList as _renderList, Fragment as _Fragment, normalizeStyle as _normalizeStyle, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "date-selector-wrapper" }
const _hoisted_2 = ["for"]
const _hoisted_3 = {
  key: 0,
  class: "date-selector__required"
}
const _hoisted_4 = ["id", "tabindex"]
const _hoisted_5 = { class: "date-selector__trigger-text" }
const _hoisted_6 = {
  key: 1,
  class: "date-selector__helper"
}
const _hoisted_7 = { class: "date-selector__error" }
const _hoisted_8 = { class: "date-selector__calendar" }
const _hoisted_9 = { class: "date-selector__calendar-header" }
const _hoisted_10 = { class: "date-selector__month-year" }
const _hoisted_11 = { class: "date-selector__weekdays" }
const _hoisted_12 = { class: "date-selector__days" }
const _hoisted_13 = ["onClick"]

import { Icon } from "@/components/ui";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

interface CalendarDay {
  date: number;
  fullDate: string;
  isOtherMonth: boolean;
  isToday: boolean;
  isSelected: boolean;
  isDisabled: boolean;
}

interface Props {
  modelValue?: string;
  minDate?: string;
  maxDate?: string;
  disablePast?: boolean;
  label?: string;
  required?: boolean;
  id?: string;
  error?: string;
  dateOnly?: boolean;
  disabled?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'DateSelector',
  props: {
    modelValue: { default: "" },
    minDate: { default: "" },
    maxDate: { default: "" },
    disablePast: { type: Boolean, default: true },
    label: {},
    required: { type: Boolean, default: false },
    id: {},
    error: { default: "" },
    dateOnly: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "blur"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const isOpen = ref(false);
const dropdownStyle = ref<Record<string, string>>({});
const triggerRef = ref<HTMLElement | null>(null);
const currentDate = ref(new Date());
let clickOutsideHandler: ((event: MouseEvent) => void) | null = null;

const dateSelectorId = computed(
  () => props.id || `date-selector-${Math.random().toString(36).substr(2, 9)}`
);

const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const monthYearLabel = computed(() => {
  const options: Intl.DateTimeFormatOptions = { month: "long", year: "numeric" };
  return currentDate.value.toLocaleDateString("en-US", options);
});

const parseDateString = (dateStr: string): Date => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  // Otherwise, use standard Date parsing
  return new Date(dateStr);
};

const displayValue = computed(() => {
  if (!props.modelValue) return "";
  const date = parseDateString(props.modelValue);
  if (isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
});

const calendarDays = computed((): CalendarDay[] => {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay();

  const days: CalendarDay[] = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDate = props.modelValue ? parseDateString(props.modelValue) : null;
  if (selectedDate) {
    selectedDate.setHours(0, 0, 0, 0);
  }

  const minDateObj = props.minDate ? parseDateString(props.minDate) : null;
  if (minDateObj) {
    minDateObj.setHours(0, 0, 0, 0);
  }

  const maxDateObj = props.maxDate ? parseDateString(props.maxDate) : null;
  if (maxDateObj) {
    maxDateObj.setHours(0, 0, 0, 0);
  }

  for (let i = 0; i < startingDayOfWeek; i++) {
    const prevMonthDate = new Date(year, month, 1 - startingDayOfWeek + i);
    days.push(
      createDayObject(
        prevMonthDate,
        true,
        today,
        selectedDate,
        minDateObj,
        maxDateObj,
        props.disablePast
      )
    );
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, month, day);
    days.push(
      createDayObject(date, false, today, selectedDate, minDateObj, maxDateObj, props.disablePast)
    );
  }

  const remainingDays = 42 - days.length;
  for (let day = 1; day <= remainingDays; day++) {
    const nextMonthDate = new Date(year, month + 1, day);
    days.push(
      createDayObject(
        nextMonthDate,
        true,
        today,
        selectedDate,
        minDateObj,
        maxDateObj,
        props.disablePast
      )
    );
  }

  return days;
});

const createDayObject = (
  date: Date,
  isOtherMonth: boolean,
  today: Date,
  selectedDate: Date | null,
  minDate: Date | null,
  maxDate: Date | null,
  disablePast: boolean
): CalendarDay => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const dateStr = `${year}-${month}-${day}`;

  const dateCopy = new Date(date);
  dateCopy.setHours(0, 0, 0, 0);

  const isToday = dateCopy.getTime() === today.getTime();
  const isSelected = selectedDate ? dateCopy.getTime() === selectedDate.getTime() : false;
  const isDisabled =
    (minDate && dateCopy < minDate) ||
    (maxDate && dateCopy > maxDate) ||
    (disablePast && dateCopy < today);

  return {
    date: date.getDate(),
    fullDate: dateStr,
    isOtherMonth,
    isToday,
    isSelected,
    isDisabled,
  };
};

const selectDate = (day: CalendarDay) => {
  if (props.disabled || day.isDisabled || day.isOtherMonth) return;
  const [year, month, dayNum] = day.fullDate.split("-").map(Number);
  if (props.dateOnly) {
    const pad = (n: number) => String(n).padStart(2, "0");
    emit("update:modelValue", `${year}-${pad(month)}-${pad(dayNum)}`);
  } else {
    emit("update:modelValue", new Date(year, month - 1, dayNum, 0, 0, 0, 0).toISOString());
  }
  setTimeout(() => {
    isOpen.value = false;
  }, 100);
};

const previousMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() - 1,
    1
  );
};

const nextMonth = () => {
  currentDate.value = new Date(
    currentDate.value.getFullYear(),
    currentDate.value.getMonth() + 1,
    1
  );
};

const updateDropdownPosition = () => {
  if (!triggerRef.value) return;

  const rect = triggerRef.value.getBoundingClientRect();
  const spacing = 8;
  const dropdownHeight = 280;

  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  const shouldShowAbove = spaceBelow < dropdownHeight + spacing && spaceAbove > spaceBelow;

  dropdownStyle.value = {
    position: "fixed",
    top: shouldShowAbove
      ? `${rect.top - dropdownHeight - spacing}px`
      : `${rect.bottom + spacing}px`,
    left: `${rect.left}px`,
    width: "290px",
    zIndex: "10100",
  };
};

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) isOpen.value = false;
  }
);

const toggleDropdown = () => {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    if (props.modelValue) {
      const date = parseDateString(props.modelValue);
      if (!isNaN(date.getTime())) {
        currentDate.value = new Date(date.getFullYear(), date.getMonth(), 1);
      }
    } else {
      currentDate.value = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
    }
    setTimeout(() => {
      updateDropdownPosition();
    }, 0);
  }
};

const handleBlur = (event: FocusEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    relatedTarget &&
    !relatedTarget.closest(".date-selector-wrapper") &&
    !relatedTarget.closest(".date-selector")
  ) {
    setTimeout(() => {
      if (isOpen.value) {
        isOpen.value = false;
      }
      emit("blur", event);
    }, 200);
  } else if (!relatedTarget) {
    setTimeout(() => {
      emit("blur", event);
    }, 100);
  }
};

onMounted(() => {
  clickOutsideHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target && !target.closest(".date-selector-wrapper") && !target.closest(".date-selector")) {
      isOpen.value = false;
    }
  };
  document.addEventListener("click", clickOutsideHandler);
  window.addEventListener("scroll", updateDropdownPosition, true);
  window.addEventListener("resize", updateDropdownPosition);
});

onUnmounted(() => {
  if (clickOutsideHandler) {
    document.removeEventListener("click", clickOutsideHandler);
  }
  window.removeEventListener("scroll", updateDropdownPosition, true);
  window.removeEventListener("resize", updateDropdownPosition);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.label)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          for: dateSelectorId.value,
          class: "date-selector__label"
        }, [
          _createTextVNode(_toDisplayString(__props.label) + " ", 1),
          (__props.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "*"))
            : _createCommentVNode("", true)
        ], 8, _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      ref_key: "triggerRef",
      ref: triggerRef,
      id: dateSelectorId.value,
      class: _normalizeClass(["date-selector__trigger", {
        'is-placeholder': !displayValue.value,
        'date-selector__trigger--open': isOpen.value,
        'date-selector__trigger--error': !!__props.error,
        'date-selector__trigger--disabled': __props.disabled,
      }]),
      onClick: toggleDropdown,
      onBlur: handleBlur,
      tabindex: __props.disabled ? -1 : 0
    }, [
      _createElementVNode("span", _hoisted_5, _toDisplayString(displayValue.value || "Select Date"), 1),
      _createVNode(_unref(Icon), {
        icon: "mdi:calendar",
        class: "date-selector__trigger-icon"
      })
    ], 42, _hoisted_4),
    (__props.error)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("span", _hoisted_7, _toDisplayString(__props.error), 1)
        ]))
      : _createCommentVNode("", true),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isOpen.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "date-selector",
            style: _normalizeStyle(dropdownStyle.value)
          }, [
            _cache[0] || (_cache[0] = _createElementVNode("div", { class: "date-selector__header" }, [
              _createElementVNode("span", { class: "date-selector__header-label" }, "DATE")
            ], -1)),
            _createElementVNode("div", _hoisted_8, [
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("button", {
                  class: "date-selector__nav-button",
                  onClick: _withModifiers(previousMonth, ["stop"]),
                  type: "button"
                }, [
                  _createVNode(_unref(Icon), { icon: "mdi:chevron-left" })
                ]),
                _createElementVNode("span", _hoisted_10, _toDisplayString(monthYearLabel.value), 1),
                _createElementVNode("button", {
                  class: "date-selector__nav-button",
                  onClick: _withModifiers(nextMonth, ["stop"]),
                  type: "button"
                }, [
                  _createVNode(_unref(Icon), { icon: "mdi:chevron-right" })
                ])
              ]),
              _createElementVNode("div", _hoisted_11, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(weekdays, (day) => {
                  return _createElementVNode("div", {
                    key: day,
                    class: "date-selector__weekday"
                  }, _toDisplayString(day), 1)
                }), 64))
              ]),
              _createElementVNode("div", _hoisted_12, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(calendarDays.value, (day, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: index,
                    class: _normalizeClass(["date-selector__day", {
                'date-selector__day--other-month': day.isOtherMonth,
                'date-selector__day--today': day.isToday,
                'date-selector__day--selected': day.isSelected,
                'date-selector__day--disabled': day.isDisabled,
              }]),
                    onClick: _withModifiers(($event: any) => (selectDate(day)), ["stop"])
                  }, _toDisplayString(day.date), 11, _hoisted_13))
                }), 128))
              ])
            ])
          ], 4))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})